'use client';

import {AgendaListFragment, AgendaListItemFragment, AgendaSectionFieldsFragment} from '@generated/graphql';
import {FC} from 'react';
import {AccordionDetails, AccordionSummary, Box, Container, Typography} from '@mui/material';
import {Document} from '@contentful/rich-text-types';
import {RichTextRenderer} from '@/components/contentful/RichTextRenderer';
import ContentfulImage from '@/components/ui/image/ContentfulImage';
import {
  AgendaItemBody,
  AgendaItemDate,
  AgendaItemRow,
  AgendaSectionContainer,
  AgendaSummaryAvatar,
  AgendaSummaryContent,
  StyledAgendaAccordion,
  StyledExpandMoreIcon,
} from '@/components/sections/agenda-section/AgendaSection.styles';

type AgendaSectionProps = {
  section: AgendaSectionFieldsFragment;
};

export const AgendaSection: FC<AgendaSectionProps> = ({section}) => {
  const lists = (section.listsCollection?.items ?? []).filter(
    (l): l is AgendaListFragment => l !== null
  );

  if (lists.length === 0) return null;

  return (
    <Container maxWidth='md' {...section.id && {id: section.id}}>
      <AgendaSectionContainer>
        {lists.map((list) => (
          <AgendaListAccordion key={list.sys.id} list={list} />
        ))}
      </AgendaSectionContainer>
    </Container>
  );
};

const AgendaListAccordion: FC<{list: AgendaListFragment}> = ({list}) => {
  const items = (list.itemsCollection?.items ?? []).filter(
    (i): i is AgendaListItemFragment => i !== null
  );

  return (
    <StyledAgendaAccordion disableGutters elevation={0}>
      <AccordionSummary expandIcon={<StyledExpandMoreIcon />}>
        <AgendaSummaryContent>
          {list.image?.url && (
            <AgendaSummaryAvatar>
              <ContentfulImage
                src={list.image.url}
                alt={list.heading ?? ''}
                fill
                sizes='56px'
                style={{objectFit: 'cover'}}
              />
            </AgendaSummaryAvatar>
          )}
          <Box>
            {list.heading && (
              <Typography variant='h4'>{list.heading}</Typography>
            )}
            {list.subHeading && (
              <Typography variant='body1' sx={{mt: 0.5, color: 'background.darkGrey'}}>
                {list.subHeading}
              </Typography>
            )}
          </Box>
        </AgendaSummaryContent>
      </AccordionSummary>
      <AccordionDetails>
        {items.map((item) => (
          <AgendaItemView key={item.sys.id} item={item} />
        ))}
      </AccordionDetails>
    </StyledAgendaAccordion>
  );
};

const AgendaItemView: FC<{item: AgendaListItemFragment}> = ({item}) => {
  const sideInfo = item.sideText;

  return (
    <AgendaItemRow>
      <AgendaItemDate>
        {sideInfo && (
          <Typography variant='body2' sx={{fontWeight: 600}}>
            {sideInfo}
          </Typography>
        )}
      </AgendaItemDate>
      <AgendaItemBody>
        {item.heading && (
          <Typography variant='h5'>{item.heading}</Typography>
        )}
        {item.subHeading && (
          <Typography variant='h6' sx={{mt: 0.5, mb: 1, color: 'background.darkGrey'}}>
            {item.subHeading}
          </Typography>
        )}
        {item.breadText?.json && (
          <RichTextRenderer richTextDocument={item.breadText.json as unknown as Document} />
        )}
      </AgendaItemBody>
    </AgendaItemRow>
  );
};
